// ABOUTME: SVG renderer adapter for the devotion map - builds markup strings and mounts live DOM.
// ABOUTME: renderSvgMarkup is a pure function; mountSvg wires it to a live HTMLElement with events.
import type { Affinity, Constellation, DevotionModel, SelectionState, StarId } from "../core/types";
import type { ReachView } from "../core/reachability";
import { affinityColor, glowColor, presentAffinities } from "./affinityColors";
import { constellationDisplay, starDisplay, edgeDisplay } from "../core/displayState";
import { fitViewBox, toViewBoxString } from "../core/viewbox";
import { MARK_COLORS, type MarkStyle, roundedSectorPath } from "./markPalette";
import type { AssetManifest } from "../ports/DataSource";

// A constellation's identity colors = the affinities it GRANTS when fully filled (1-3).
// Reachability is shown by brightness (faded if you cannot start it), so the color is
// free to convey what the constellation contributes to your affinity pool instead. Rare
// constellations that grant no affinity fall back to what they require, then grey.
function gradColors(c: Constellation): string[] {
  const grant = presentAffinities(c.affinityBonus).map(affinityColor);
  if (grant.length) return grant;
  const req = presentAffinities(c.affinityRequired).map(affinityColor);
  return req.length ? req : ["#9aa3b2"];
}

// Diamond polygon points centered at (cx, cy) with the given radius.
function diamondPoints(cx: number, cy: number, r: number): string {
  return `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`;
}

// Left-to-right gradient stops for a constellation's identity colors (1-3 colors).
function gradientStops(colors: string[]): string {
  if (colors.length === 1)
    return `<stop offset="0%" stop-color="${colors[0]}"/><stop offset="100%" stop-color="${colors[0]}"/>`;
  return colors
    .map((c, i) => `<stop offset="${Math.round((i / (colors.length - 1)) * 100)}%" stop-color="${c}"/>`)
    .join("");
}

// Star buttons use the 64x64 devotion_star_up.tex bitmap, placed by its top-left
// (bitmapPositionX/Y, which is what `position` holds). The visible star and the
// background art's glow sit at the button center, so dots and links are drawn
// shifted by half the button to line up with the art.
const STAR_CENTER = 32;
// Visible star dot radius.
const STAR_RADIUS = 12;
// Celestial-power stars render as a larger diamond so they stand out (~25% bigger
// than the ordinary dots) without overwhelming them.
const POWER_RADIUS = 19;
// Invisible click/hover target radius around each star (larger than the visible dot;
// also covers the power diamond, whose vertices sit within this radius).
const HIT_RADIUS = 22;
// Padding around a constellation's star bounding box for its hover/click region.
const CON_PAD = 24;

// Brightness -> opacity, per element type. The only place these tunable values live (the spec's
// "resolved opacity number"); brightness itself is resolved purely in core, so nothing collides here.
const ART_OPACITY = { active: 1, attainable: 0.5, unattainable: 0.12 } as const;
const STAR_OPACITY = { active: 1, attainable: 1, unattainable: 0.3 } as const;
const EDGE_OPACITY = { active: 1, attainable: 1, unattainable: 0.3 } as const;
// The affinity match halo glows full strength on a reachable constellation and dimmer on an unreachable
// one, so the brightness channel still reads under a filter (reachable matches are not just colored).
const HALO_UNREACHABLE_OPACITY = 0.25;
// Search arcs ride a ring just outside the star dot (and outside the larger power diamond): the
// track radius is the centre line of a base-width arc, so the ring's inner edge (7 units beyond
// the dot) stays fixed.
const ARC_RADIUS = STAR_RADIUS + 15;
const POWER_ARC_RADIUS = POWER_RADIUS + 14;
// Base arc width; an arc thickens up to triple with its star's magnitude weight for the search,
// growing OUTWARD from the fixed inner edge (outer edge = inner + w) so the dot stays clear.
const ARC_WIDTH = 16;
const ARC_WEIGHT_SPAN = 2;
// Corner radius of an arc's four rounded corners, as a fraction of its width: under the half that
// would make a fully round end, so ends read as squared-off pills.
const ARC_CORNER = 0.3;
// Degrees of seam between two arcs that meet, split evenly across the seam.
const ARC_SEAM_DEG = 6;
// Searches sharing an angle stack outward, this far apart.
const ARC_STACK_GAP = 2;

/** One search's marker on one star: its mark style, that star's magnitude weight (0..1), and its style slot. */
export interface StarMark {
  style: MarkStyle;
  weight: number;
  slot: number;
}

// Each angle's arc extent on one star (degrees clockwise from up, unnormalised): the half circle
// centred on the angle, cut back to the bisector against each neighbouring angle whose half
// circle overlaps it, less the seam. Alone, an angle keeps its whole half. Angles come back sorted.
function arcExtents(angles: readonly number[]): Map<number, { from: number; to: number }> {
  const sorted = [...new Set(angles)].sort((a, b) => a - b);
  const side = (gap: number) => (gap > 180 ? 90 : gap / 2 - ARC_SEAM_DEG / 2);
  const out = new Map<number, { from: number; to: number }>();
  sorted.forEach((angle, i) => {
    const cw = sorted.length === 1 ? 360 : (sorted[(i + 1) % sorted.length]! - angle + 360) % 360;
    const ccw = sorted.length === 1 ? 360 : (angle - sorted[(i - 1 + sorted.length) % sorted.length]! + 360) % 360;
    out.set(angle, { from: angle - side(ccw), to: angle + side(cw) });
  });
  return out;
}

// The arcs marker for one star: a faint track ring plus, per matching search, a filled ring
// sector over its angular extent (arcExtents), thickened outward by the star's magnitude weight,
// its corners rounded (roundedSectorPath) so ends read as squared-off pills while seams sit
// exactly at the extents. Marks sharing an angle (slots eight apart) stack outward in slot order.
// The track look is CSS; the sector geometry and fill are attributes here.
function arcMarkup(cx: number, cy: number, baseR: number, marks: readonly StarMark[]): string {
  const inner = baseR - ARC_WIDTH / 2;
  const byAngle = new Map<number, StarMark[]>();
  for (const m of marks) {
    const stack = byAngle.get(m.style.angle);
    if (stack) stack.push(m);
    else byAngle.set(m.style.angle, [m]);
  }
  const arcs: string[] = [];
  for (const [angle, { from, to }] of arcExtents([...byAngle.keys()])) {
    let edge = inner;
    for (const m of byAngle.get(angle)!.sort((a, b) => a.slot - b.slot)) {
      const w = Math.round(ARC_WIDTH * (1 + ARC_WEIGHT_SPAN * m.weight) * 10) / 10;
      const d = roundedSectorPath(cx, cy, edge, edge + w, from, to, ARC_CORNER * w);
      arcs.push(
        `<g class="search-arc" data-slot="${m.slot}" data-from="${from}" data-to="${to}">` +
          `<path class="arc" d="${d}" fill="${m.style.color}"/></g>`,
      );
      edge += w + ARC_STACK_GAP;
    }
  }
  const track = `<circle class="arc-track" cx="${cx}" cy="${cy}" r="${baseR}"/>`;
  return `<g class="search-arcs">${track}${arcs.join("")}</g>`;
}

export interface RenderOpts {
  manifest: AssetManifest | null;
  // Per-star search marks: each matched star maps to the marks (fixed angle + color style, the
  // star's magnitude weight, and the style slot) of the searches that hit it. Every search draws
  // its own arc centred on its own angle, so the marker says which searches matched and how big
  // each grant is; see arcMarkup.
  marks?: ReadonlyMap<StarId, readonly StarMark[]>;
  // The text query's mark color for this render (the query holds a style slot like any tag), so
  // its constellation halo matches its star arcs. Defaults to the first palette color.
  queryColor?: string;
  reach?: ReachView;
  diff?: { added: Set<StarId>; removed: Set<StarId> } | null;
  // When present, an affinity filter is active. A constellation matches when it provides any of these
  // filter affinities (matchedAffinities); matching constellations glow (see the aff-glow layer) and
  // the rest desaturate (the mute color outcome) - the filter never changes brightness.
  affinityFilter?: { grants: Set<Affinity>; requires: Set<Affinity> };
  // When present, a text search is active; these constellations matched on name or description
  // and glow via the search-glow halo in the query's mark color. Star-level query matches arrive
  // as `marks` entries instead (a constellation hit glows the art, not its stars).
  conHighlight?: Set<string>;
}

// A constellation's hover/click footprint in SVG world coords: its art bounds
// (x0,y0)-(x1,y1) and the centroid of its stars, used to break ties where art
// bounding boxes overlap.
export interface ConRegion {
  id: string;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  cx: number;
  cy: number;
}

// Build a hover region per constellation: the art bounds when art is present, else
// the star bounding box plus padding. Star centroids are the tie-break centers.
export function buildConRegions(model: DevotionModel, manifest: AssetManifest | null): ConRegion[] {
  const regions: ConRegion[] = [];
  for (const c of model.constellations.values()) {
    const stars = c.starIds.map((id) => model.stars.get(id)).filter((s): s is NonNullable<typeof s> => !!s);
    if (stars.length === 0) continue;
    const xs = stars.map((s) => s.position.x + STAR_CENTER);
    const ys = stars.map((s) => s.position.y + STAR_CENTER);
    const cx = xs.reduce((a, b) => a + b, 0) / xs.length;
    const cy = ys.reduce((a, b) => a + b, 0) / ys.length;
    const name = c.background?.image?.split("/").pop() ?? "";
    const art = manifest?.images[name];
    if (art && c.background && c.background.x != null && c.background.y != null) {
      regions.push({
        id: c.id,
        x0: c.background.x,
        y0: c.background.y,
        x1: c.background.x + art.w,
        y1: c.background.y + art.h,
        cx,
        cy,
      });
    } else {
      regions.push({
        id: c.id,
        x0: Math.min(...xs) - CON_PAD,
        y0: Math.min(...ys) - CON_PAD,
        x1: Math.max(...xs) + CON_PAD,
        y1: Math.max(...ys) + CON_PAD,
        cx,
        cy,
      });
    }
  }
  return regions;
}

// The constellation owning a world point: among the regions whose bounds contain
// it, the one whose centroid is nearest. null if the point is outside every region.
export function constellationAt(regions: ConRegion[], wx: number, wy: number): string | null {
  let best: string | null = null;
  let bestD = Infinity;
  for (const r of regions) {
    if (wx < r.x0 || wx > r.x1 || wy < r.y0 || wy > r.y1) continue;
    const dx = wx - r.cx;
    const dy = wy - r.cy;
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      best = r.id;
    }
  }
  return best;
}

export function renderSvgMarkup(model: DevotionModel, state: SelectionState, opts: RenderOpts): string {
  const reach = opts.reach;
  const diff = opts.diff ?? null;
  const marks = opts.marks;
  const queryColor = opts.queryColor ?? MARK_COLORS[0];
  const settings = {
    selected: state.selected,
    reach,
    affinityFilter: opts.affinityFilter,
    benefitMatch: marks ? new Set(marks.keys()) : undefined,
    conMatch: opts.conHighlight,
    diff,
  };
  const defs: string[] = [];
  const parts: string[] = [];

  const affFilter = opts.affinityFilter;

  // Constellation hover/click is resolved in JS against each constellation's art
  // bounds (see buildConRegions / constellationAt), so the whole image is hoverable
  // even though art bounding boxes overlap. Star hit-circles take precedence.

  // Benefit-match glow as an SVG-native filter. CSS `filter: drop-shadow()` on SVG shapes is
  // unreliable on WebKit (iOS Safari/Firefox render nothing), so the halo is built from core SVG
  // filter primitives the SVG engine rasterizes everywhere. Two flooded-blur layers (tight light +
  // wide blue) under the star approximate the prior drop-shadow stack; sized in user units (~star r 12).
  defs.push(
    `<filter id="match-glow" x="-400%" y="-400%" width="900%" height="900%" color-interpolation-filters="sRGB">` +
      `<feGaussianBlur in="SourceAlpha" stdDeviation="9" result="b1"/><feFlood flood-color="#e3f2ff" result="c1"/><feComposite in="c1" in2="b1" operator="in" result="g1"/>` +
      `<feGaussianBlur in="SourceAlpha" stdDeviation="22" result="b2"/><feFlood flood-color="#6cb6ff" result="c2"/><feComposite in="c2" in2="b2" operator="in" result="g2"/>` +
      `<feMerge><feMergeNode in="g2"/><feMergeNode in="g2"/><feMergeNode in="g2"/><feMergeNode in="g1"/><feMergeNode in="g1"/><feMergeNode in="g1"/><feMergeNode in="SourceGraphic"/></feMerge>` +
      `</filter>`,
  );

  // Colored glows for active art, taken links, and selectable stars. Same WebKit reason as match-glow:
  // CSS drop-shadow on SVG renders nothing on iOS. These blur the element's OWN paint (SourceGraphic),
  // so a single filter glows in each element's own color with no per-color variants. self-glow-art also
  // lifts brightness to replace the prior brightness(1.15).
  defs.push(
    `<filter id="self-glow" x="-100%" y="-100%" width="300%" height="300%" color-interpolation-filters="sRGB">` +
      `<feGaussianBlur in="SourceGraphic" stdDeviation="6" result="b"/>` +
      `<feMerge><feMergeNode in="b"/><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>` +
      `</filter>`,
    `<filter id="self-glow-art" x="-40%" y="-40%" width="180%" height="180%" color-interpolation-filters="sRGB">` +
      `<feGaussianBlur in="SourceGraphic" stdDeviation="10" result="b"/>` +
      `<feComponentTransfer in="SourceGraphic" result="bright"><feFuncR type="linear" slope="1.35"/><feFuncG type="linear" slope="1.35"/><feFuncB type="linear" slope="1.35"/></feComponentTransfer>` +
      `<feMerge><feMergeNode in="b"/><feMergeNode in="b"/><feMergeNode in="bright"/></feMerge>` +
      `</filter>`,
  );

  // Affinity match glow: a diffuse colored halo. The source is a gradient-filled, art-masked rect (the
  // constellation's MATCHED affinity colors), blurred, saturated, and stacked into a soft halo. The
  // intensity comes from saturation plus alpha density (the repeated merge), not an RGB brightness lift:
  // multiplying the channels clips the strong colors toward white (purple suffers most). SVG-native (CSS
  // drop-shadow on SVG fails on WebKit). stdDeviation is in user units, so the halo scales with zoom;
  // start diffuse and tune. The filter region is expanded so the blur is not clipped.
  // Only emitted when an affinity filter is active (no filter -> no glow layer -> no def needed).
  if (affFilter) {
    defs.push(
      `<filter id="aff-glow" x="-100%" y="-100%" width="300%" height="300%" color-interpolation-filters="sRGB">` +
        `<feGaussianBlur in="SourceGraphic" stdDeviation="55" result="b"/>` +
        `<feColorMatrix in="b" type="saturate" values="1.8" result="sat"/>` +
        `<feMerge><feMergeNode in="sat"/><feMergeNode in="sat"/><feMergeNode in="sat"/><feMergeNode in="sat"/></feMerge>` +
        `</filter>`,
    );
    // mute: drain color toward grey (the affinity-filter de-emphasis). SVG-native feColorMatrix
    // because CSS filter: saturate() renders nothing on WebKit, like our other glows. `mute-wide` is the
    // same desaturation with an expanded region, used to wrap a benefit-match glow layer (whose halo
    // spreads well past the marker) so the whole glow desaturates without the halo being clipped. The
    // search halo below reuses this same def (a muted constellation can still be a search match) - it
    // relies on this def existing only while an affinity filter is active, which holds because
    // `cd0.color.kind` can only be "mute" when one is (see constellationColor). Don't move this def
    // out from under that `if (affFilter)` without checking that reasoning still holds.
    defs.push(
      `<filter id="mute" color-interpolation-filters="sRGB"><feColorMatrix type="saturate" values="0"/></filter>`,
      `<filter id="mute-wide" x="-400%" y="-400%" width="900%" height="900%" color-interpolation-filters="sRGB"><feColorMatrix type="saturate" values="0"/></filter>`,
    );
  }

  // Search-match halo filter def: flooded with the query's mark color so a query match reads
  // identically whether it lands on a star (an arc) or a whole constellation (this halo), and never
  // as an affinity colour.
  // Only emitted when a search is active (mirrors #aff-glow/#mute being gated on affFilter above).
  //
  // Two bands: a near one (16) for brightness against the silhouette, a far one (38) for reach.
  // Both flooded the query color, no white core - white washes even under the art, and on thin line work a
  // small-radius blur bleeds inward across the strokes and floods the shape rather than rimming it.
  // stdDeviation is in user units against art roughly 250x380. The merge stacking is what carries
  // brightness: a wide blur thins alpha, so reach and intensity have to be bought separately. Pushing
  // this hard is only safe because the halo paints UNDER the art (see the searchHaloParts flush).
  // The filter region is widened past the usual 300% or the far band clips at the edges.
  const conMatched = opts.conHighlight;
  if (conMatched && conMatched.size > 0) {
    defs.push(
      `<filter id="search-glow" x="-150%" y="-150%" width="400%" height="400%" color-interpolation-filters="sRGB">` +
        `<feGaussianBlur in="SourceAlpha" stdDeviation="16" result="b1"/>` +
        `<feFlood flood-color="${queryColor}" result="c1"/><feComposite in="c1" in2="b1" operator="in" result="g1"/>` +
        `<feGaussianBlur in="SourceAlpha" stdDeviation="38" result="b2"/>` +
        `<feFlood flood-color="${queryColor}" result="c2"/><feComposite in="c2" in2="b2" operator="in" result="g2"/>` +
        `<feMerge>` +
        `<feMergeNode in="g2"/><feMergeNode in="g2"/><feMergeNode in="g2"/>` +
        `<feMergeNode in="g1"/><feMergeNode in="g1"/><feMergeNode in="g1"/>` +
        `</feMerge>` +
        `</filter>`,
    );
  }

  // Gradient defs for every constellation (used by both the star fills and the art tint).
  for (const c of model.constellations.values()) {
    defs.push(
      `<linearGradient id="grad-${c.id}" x1="0" y1="0" x2="1" y2="0">${gradientStops(gradColors(c))}</linearGradient>`,
    );
  }

  // Art-silhouette masks, built once per constellation and shared by the glow halo (Layer 0) and the
  // art tint (Layer 1). A constellation needs one only if it has art AND it either matches the filter
  // or carries an affinity-requirement tint.
  const maskBuilt = new Set<string>();
  const ensureMask = (cid: string, url: string, x: number, y: number, w: number, h: number) => {
    if (maskBuilt.has(cid)) return;
    maskBuilt.add(cid);
    defs.push(`<mask id="mask-${cid}"><image href="${url}" x="${x}" y="${y}" width="${w}" height="${h}"/></mask>`);
  };

  // Affinity match glow: a colored halo for matching constellations. Drawn ON TOP of the art (flushed
  // after the art layer below) so the halo's color reads through the bright near-white line-art instead
  // of being washed to a pastel from underneath. The gradient is built from ONLY the matched affinity
  // colors (solid when one matches). This is the color axis (affinity) expressing "match"; it never
  // touches opacity (attainability), so the two channels stay independent.
  const haloParts: string[] = [];
  if (opts.manifest && affFilter) {
    for (const c of model.constellations.values()) {
      const cd0 = constellationDisplay(c, settings);
      if (cd0.color.kind !== "match") continue;
      const name = c.background?.image?.split("/").pop() ?? "";
      const art = opts.manifest.images[name];
      if (!(art && c.background && c.background.x != null && c.background.y != null)) continue;
      const { x, y } = c.background;
      const cols = cd0.color.affinities.map(glowColor);
      defs.push(
        `<linearGradient id="aff-grad-${c.id}" x1="0" y1="0" x2="1" y2="0">${gradientStops(cols)}</linearGradient>`,
      );
      ensureMask(c.id, art.url, x, y, art.w, art.h);
      // The halo feels the brightness channel like every other layer: a matching constellation you cannot
      // reach glows in its color but dimmer than a reachable one, so reachability still reads under a filter.
      const haloOp = cd0.brightness === "unattainable" ? HALO_UNREACHABLE_OPACITY : 1;
      const glow = `<rect class="aff-glow" opacity="${haloOp}" x="${x}" y="${y}" width="${art.w}" height="${art.h}" fill="url(#aff-grad-${c.id})" mask="url(#mask-${c.id})" filter="url(#aff-glow)"/>`;
      // A selected constellation also carries its own #self-glow-art bloom, which raises the local
      // brightness and would swallow a single faint halo - so a selected match showed no color. Stack the
      // halo for active ones so the matched color still reads, while they stay bright via the self-glow.
      haloParts.push(glow);
      if (cd0.selfGlow) haloParts.push(glow);
    }
  }

  // Search-match halo: an aura AROUND the constellation, flushed UNDER the art (unlike the affinity
  // halo, which paints on top so its colour reads through the line work). A surrounding glow painted
  // over thin line art washes it out; painted under, the aura surrounds and the art stays crisp.
  const searchHaloParts: string[] = [];
  if (opts.manifest && conMatched && conMatched.size > 0) {
    for (const c of model.constellations.values()) {
      const cd0 = constellationDisplay(c, settings);
      if (!cd0.emphasis) continue;
      const name = c.background?.image?.split("/").pop() ?? "";
      const art = opts.manifest.images[name];
      if (!(art && c.background && c.background.x != null && c.background.y != null)) continue;
      const { x, y } = c.background;
      ensureMask(c.id, art.url, x, y, art.w, art.h);
      // Feels the brightness channel like the affinity halo: a matched constellation you cannot
      // reach still glows, but dimmer, so reachability keeps reading under a search.
      const op = cd0.brightness === "unattainable" ? HALO_UNREACHABLE_OPACITY : 1;
      // The mask goes on the rect and the filter on a wrapping <g>, and that split is the whole
      // trick. SVG applies filter BEFORE masking, so with both on one element the blur spreads and
      // is then clipped straight back to the art silhouette - no aura can escape the shape, which
      // is why this first shipped as an invisible inner tint. Masking the child and filtering the
      // parent blurs the already-masked paint, letting it bleed outward.
      const glow =
        `<g filter="url(#search-glow)">` +
        `<rect class="search-glow" opacity="${op}" x="${x}" y="${y}" width="${art.w}" height="${art.h}" ` +
        `fill="${queryColor}" mask="url(#mask-${c.id})"/>` +
        `</g>`;
      // Off-filter constellations desaturate like an off-filter star's benefit glow does, so the
      // halo reads as "matched, off-filter" instead of vanishing.
      searchHaloParts.push(cd0.color.kind === "mute" ? `<g filter="url(#mute-wide)">${glow}</g>` : glow);
    }
  }
  // Under the art, per the note above. The affinity halo's own flush stays after it.
  parts.push(...searchHaloParts);

  // Layer 1: optional art, tinted by the constellation's identity (granted) colors.
  // The tint rect is only drawn for constellations that have an affinity requirement
  // (it carries a mask built from the art); crossroads have no requirement and no tint.
  if (opts.manifest) {
    for (const c of model.constellations.values()) {
      const name = c.background?.image?.split("/").pop() ?? "";
      const art = opts.manifest.images[name];
      if (!(art && c.background && c.background.x != null && c.background.y != null)) continue;
      const { x, y } = c.background;
      const cd = constellationDisplay(c, settings);
      const op = ART_OPACITY[cd.brightness];
      const muted = cd.color.kind === "mute" ? " mute" : "";
      const active = cd.selfGlow ? " active" : "";
      // An active constellation glows in its own art colors via the #self-glow-art SVG filter (see the
      // .art.active CSS rule); the filter derives the color from the image, so no per-color style is needed.
      const img = `href="${art.url}" x="${x}" y="${y}" width="${art.w}" height="${art.h}"`;
      // data-con-id lets a blocked constellation deselect flash this icon (see main.ts).
      parts.push(`<image ${img} class="art${active}${muted}" opacity="${op}" data-con-id="${c.id}"/>`);
      if (presentAffinities(c.affinityRequired).length > 0) {
        ensureMask(c.id, art.url, x, y, art.w, art.h);
        parts.push(
          `<rect class="art-tint${active}${muted}" opacity="${op}" x="${x}" y="${y}" width="${art.w}" height="${art.h}" fill="url(#grad-${c.id})" mask="url(#mask-${c.id})"/>`,
        );
      }
    }
  }
  // Flush the match halos on top of the art (see the haloParts note above).
  parts.push(...haloParts);

  // Layer 2: links. A segment whose both endpoints are selected is "taken" (drawn gold, like
  // grimtools); brightness and color come from the edge display record.
  for (const star of model.stars.values()) {
    const con = model.constellations.get(star.constellationId)!;
    for (const p of star.predecessors) {
      const a = model.stars.get(p);
      if (!a) continue;
      const ed = edgeDisplay(con, p, star.id, settings);
      const muted = ed.color.kind === "mute" ? " mute" : "";
      const op = EDGE_OPACITY[ed.brightness];
      const coords = `x1="${a.position.x + STAR_CENTER}" y1="${a.position.y + STAR_CENTER}" x2="${star.position.x + STAR_CENTER}" y2="${star.position.y + STAR_CENTER}"`;
      // A taken segment gets a soft gold bloom from a wide, faint underlay line rather than an SVG filter:
      // an objectBoundingBox filter region collapses to nothing on a perfectly horizontal or vertical line
      // (its bounding box has zero height or width), which made axis-aligned taken links render invisibly.
      if (ed.taken) parts.push(`<line class="link-glow" opacity="${op}" ${coords}/>`);
      parts.push(`<line class="link${ed.taken ? " taken" : ""}${muted}" opacity="${op}" ${coords}/>`);
    }
  }

  // Layer 3: stars. Each is an invisible large hit target (carries data-star-id)
  // plus a small visible dot (pointer-events:none) so the click/hover area is generous.
  for (const star of model.stars.values()) {
    const con = model.constellations.get(star.constellationId)!;
    const sd = starDisplay(star, con, settings);
    const solid = gradColors(con)[0] ?? "#9aa3b2";
    const cx = star.position.x + STAR_CENTER;
    const cy = star.position.y + STAR_CENTER;
    const style = `--affinity:${solid};--grad:url(#grad-${con.id})`;
    // Immediacy: a non-selected star is "selectable" (colored) when clickable, else "locked" (grey).
    const st = sd.selected ? "selected" : sd.clickable ? "selectable" : "locked";
    const muted = sd.color.kind === "mute" ? " mute" : "";
    const cmp = sd.diff === "add" ? " cmp-add" : sd.diff === "remove" ? " cmp-rm" : "";
    const op = STAR_OPACITY[sd.brightness];
    const cls = `star ${st}${muted}${cmp}`;
    const dot = star.celestialPower
      ? `<polygon class="${cls}" opacity="${op}" points="${diamondPoints(cx, cy, POWER_RADIUS)}" style="${style}"/>`
      : `<circle class="${cls}" opacity="${op}" cx="${cx}" cy="${cy}" r="${STAR_RADIUS}" style="${style}"/>`;
    // Search-arc emphasis is a SEPARATE full-opacity layer so it reads even on an unattainable (dim)
    // star, whose dot keeps its attainability opacity. When the star's constellation is off the
    // affinity filter, the arcs are wrapped in #mute-wide so the whole marker desaturates too - the
    // match then reads as "search match, off-filter" without the dot's opacity bleeding into the arcs.
    // The arcs go UNDER the dots: a wide arc can reach a close neighbour, whose dot then stays on top.
    let marker = "";
    const starMarks = sd.benefitMatch ? marks?.get(star.id) : undefined;
    if (starMarks && starMarks.length > 0) {
      const shape = arcMarkup(cx, cy, star.celestialPower ? POWER_ARC_RADIUS : ARC_RADIUS, starMarks);
      marker = muted ? `<g filter="url(#mute-wide)">${shape}</g>` : shape;
    }
    parts.push(
      `<circle data-star-id="${star.id}" class="hit ${st}" cx="${cx}" cy="${cy}" r="${HIT_RADIUS}"/>${marker}${dot}`,
    );
  }

  const pts = [...model.stars.values()].map((s) => ({ x: s.position.x + STAR_CENTER, y: s.position.y + STAR_CENTER }));
  const vb = toViewBoxString(fitViewBox(pts, 60));
  return `<svg id="map" viewBox="${vb}" preserveAspectRatio="xMidYMid meet"><defs>${defs.join("")}</defs>${parts.join("")}</svg>`;
}

/** Per-render inputs for a mounted map, mirroring RenderOpts minus the boot-time manifest. */
export interface UpdateOpts {
  marks?: ReadonlyMap<StarId, readonly StarMark[]>;
  queryColor?: string;
  reach?: ReachView;
  diff?: { added: Set<StarId>; removed: Set<StarId> } | null;
  affinityFilter?: { grants: Set<Affinity>; requires: Set<Affinity> };
  conHighlight?: Set<string>;
}

export interface SvgHandle {
  update(state: SelectionState, opts?: UpdateOpts): void;
  svg: SVGSVGElement;
  // Draw (or clear, with null) a box around a constellation's stars, on top of every layer. Used for
  // build-order row hover-sync; an outline on the constellation's own art is buried under later-painted
  // layers and traces the oversized texture rect, so a dedicated top overlay is drawn instead.
  highlightCon(id: string | null): void;
  // Emphasize (or clear, with null) a single star with the benefit-match treatment (enlarged + halo),
  // on top of every layer. Used for side-panel power-row hover so the power's own star pops on the map.
  highlightStar(id: string | null): void;
  // Pulse every arc of the given style slots (the .pulse CSS animation), so hovering a legend row
  // shows which arcs on the map are that search's. An empty list only clears.
  pulseMarks(slots: readonly number[]): void;
}
export type HoverTarget = { kind: "star" | "constellation"; id: string } | null;
export interface SvgDeps {
  manifest: AssetManifest | null;
  onStarClick(id: StarId, clientX: number, clientY: number): void;
  onConstellationClick(id: string, clientX: number, clientY: number): void;
  onHover(target: HoverTarget, clientX: number, clientY: number): void;
}

export function mountSvg(container: HTMLElement, model: DevotionModel, deps: SvgDeps): SvgHandle {
  const regions = buildConRegions(model, deps.manifest);
  function render(state: SelectionState, opts: UpdateOpts = {}) {
    container.innerHTML = renderSvgMarkup(model, state, { manifest: deps.manifest, ...opts });
  }
  render({ selected: new Set(), pointCap: 55 });
  const svg = container.querySelector("svg") as SVGSVGElement;

  // The constellation under a screen point, found by mapping the point into the
  // live SVG's coordinate space and resolving it against the art regions.
  function conAt(clientX: number, clientY: number): string | null {
    const live = container.querySelector("svg") as SVGSVGElement | null;
    const ctm = live?.getScreenCTM?.();
    if (!live || !ctm) return null;
    const pt = live.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const w = pt.matrixTransform(ctm.inverse());
    return constellationAt(regions, w.x, w.y);
  }

  container.addEventListener("click", (e) => {
    const me = e as MouseEvent;
    const sid = (e.target as Element)?.getAttribute?.("data-star-id");
    if (sid) {
      deps.onStarClick(sid, me.clientX, me.clientY);
      return;
    }
    const cid = conAt(me.clientX, me.clientY);
    if (cid) deps.onConstellationClick(cid, me.clientX, me.clientY);
  });
  // No hover on touch devices: a tap synthesizes a single mousemove at the tap point, which would
  // otherwise paint the passive (no-commit) hover tooltip over whatever the dismissing tap landed on.
  // The touch affordance is the commit popover (onStarClick/onConstellationClick), not hover.
  const noHover = () => matchMedia("(hover: none) and (pointer: coarse)").matches;
  container.addEventListener("mousemove", (e) => {
    if (noHover()) return;
    const sid = (e.target as Element)?.getAttribute?.("data-star-id");
    const cid = sid ? null : conAt((e as MouseEvent).clientX, (e as MouseEvent).clientY);
    const target: HoverTarget = sid ? { kind: "star", id: sid } : cid ? { kind: "constellation", id: cid } : null;
    container.classList.toggle("con-hover", !sid && !!cid);
    deps.onHover(target, (e as MouseEvent).clientX, (e as MouseEvent).clientY);
  });
  // Leaving the map clears any hover so the tooltip never lingers over a sidebar (mousemove alone
  // stops firing at the container edge, so it would otherwise stay painted).
  container.addEventListener("mouseleave", (e) => {
    container.classList.remove("con-hover");
    deps.onHover(null, (e as MouseEvent).clientX, (e as MouseEvent).clientY);
  });

  // A box around a constellation's stars, drawn as the last SVG child so no layer paints over it. Sized to
  // the star bounding box (plus padding) rather than the art texture rect, so it hugs the actual stars.
  function highlightCon(id: string | null) {
    const live = container.querySelector("svg") as SVGSVGElement | null;
    if (!live) return;
    live.querySelector(".con-highlight")?.remove();
    if (!id) return;
    const con = model.constellations.get(id);
    const stars = con?.starIds.map((s) => model.stars.get(s)).filter((s): s is NonNullable<typeof s> => !!s) ?? [];
    if (!stars.length) return;
    const xs = stars.map((s) => s.position.x + STAR_CENTER);
    const ys = stars.map((s) => s.position.y + STAR_CENTER);
    const x0 = Math.min(...xs) - CON_PAD;
    const y0 = Math.min(...ys) - CON_PAD;
    const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    rect.setAttribute("class", "con-highlight");
    rect.setAttribute("x", String(x0));
    rect.setAttribute("y", String(y0));
    rect.setAttribute("width", String(Math.max(...xs) + CON_PAD - x0));
    rect.setAttribute("height", String(Math.max(...ys) + CON_PAD - y0));
    rect.setAttribute("rx", "8");
    live.appendChild(rect);
  }

  // Emphasize a single star with the same enlarged + halo treatment a benefit-filter match gets
  // (the `.benefit-glow` class), drawn as the last SVG child so it sits on top. A power star is a
  // diamond like its dot. Clears any prior highlight; null clears without drawing.
  function highlightStar(id: string | null) {
    const live = container.querySelector("svg") as SVGSVGElement | null;
    if (!live) return;
    live.querySelector(".star-highlight")?.remove();
    if (!id) return;
    const star = model.stars.get(id);
    if (!star) return;
    const con = model.constellations.get(star.constellationId);
    const cx = star.position.x + STAR_CENTER;
    const cy = star.position.y + STAR_CENTER;
    const solid = (con && gradColors(con)[0]) || "#9aa3b2";
    const ns = "http://www.w3.org/2000/svg";
    const shape = document.createElementNS(ns, star.celestialPower ? "polygon" : "circle");
    if (star.celestialPower) {
      shape.setAttribute("points", diamondPoints(cx, cy, POWER_RADIUS));
    } else {
      shape.setAttribute("cx", String(cx));
      shape.setAttribute("cy", String(cy));
      shape.setAttribute("r", String(STAR_RADIUS));
    }
    shape.setAttribute("class", "benefit-glow star-highlight");
    shape.setAttribute("style", `--affinity:${solid};--grad:url(#grad-${con?.id})`);
    live.appendChild(shape);
  }

  // Pulse the arcs of the given slots: the .pulse class runs the CSS animation and is removed when
  // it ends, so a later hover can pulse again. Any pulse still running is cut first.
  function pulseMarks(slots: readonly number[]) {
    const live = container.querySelector("svg") as SVGSVGElement | null;
    if (!live) return;
    live.querySelectorAll(".search-arc.pulse").forEach((el) => {
      el.classList.remove("pulse");
    });
    if (slots.length === 0) return;
    live.querySelectorAll(slots.map((s) => `.search-arc[data-slot="${s}"]`).join(",")).forEach((el) => {
      el.classList.add("pulse");
      el.addEventListener("animationend", () => el.classList.remove("pulse"), { once: true });
    });
  }

  return {
    svg,
    update(state, opts) {
      const live = container.querySelector("svg") as SVGSVGElement | null;
      const vb = live?.getAttribute("viewBox");
      render(state, opts);
      const next = container.querySelector("svg") as SVGSVGElement | null;
      if (vb && next) next.setAttribute("viewBox", vb); // preserve pan/zoom across re-render
    },
    highlightCon,
    highlightStar,
    pulseMarks,
  };
}
